import { haversineKm } from "@/lib/today/regions";
import type { StayPurpose, StayReadiness } from "./stays";

export type CompanionBaseRecord = {
  id: string;
  kind: "home" | "trip";
  name: string;
  /**
   * Legacy tag from the frozen lib/today/regions list, kept for the forum, events and signup
   * intent only. A base's forecast area comes from its anchor spot (`baseAreaFor` in
   * lib/companion/local-comparison-server.ts), never from this tag.
   */
  regionSlug: string | null;
  anchorSlug: string;
  anchorType?: string | null;
  isPrimary: boolean;
  startsOn: string | null;
  endsOn: string | null;
  createdAt: string;
  timezone: string | null;
  purpose?: StayPurpose | null;
  updatedAt?: string;
  arrivalAt?: string | null;
  departureAt?: string | null;
  accommodationLabel?: string | null;
  readiness?: StayReadiness;
};

export type CompanionBaseStampRecord = Pick<
  CompanionBaseRecord,
  "id" | "kind" | "name" | "isPrimary" | "startsOn" | "endsOn" | "createdAt" | "timezone"
>;

export type CompanionBaseSavedPlace = {
  id: string;
  slug: string | null;
  name: string;
  href: string;
  createdAt?: string | null;
  baseId?: string | null;
  isFestival?: boolean;
};

export type CompanionBaseGroup = {
  key: string;
  heading: string;
  baseId: string | null;
  places: CompanionBaseSavedPlace[];
};

type ResolveBaseOptions = {
  now?: Date;
};

type GroupOptions = {
  now?: Date;
  bases: CompanionBaseRecord[];
  places: CompanionBaseSavedPlace[];
};

const BASE_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

export function resolveActiveBase(
  bases: CompanionBaseStampRecord[],
  options: ResolveBaseOptions = {},
): CompanionBaseStampRecord | null {
  const now = options.now ?? new Date();
  const trips = bases
    .filter((base) => base.kind === "trip")
    .filter((base) => isTripActive(base, now))
    .sort(compareCreatedAtDesc);

  if (trips[0]) return trips[0];

  const homes = bases
    .filter((base) => base.kind === "home")
    .sort(compareCreatedAtDesc);
  if (homes.length === 0) return null;

  return homes.find((base) => base.isPrimary) ?? homes[0] ?? null;
}

export function resolveActiveBaseIdForStamping(
  bases: CompanionBaseStampRecord[],
  options: ResolveBaseOptions = {},
): string | null {
  return resolveActiveBase(bases, options)?.id ?? null;
}

export function pickPromotedPrimaryHome(
  bases: CompanionBaseStampRecord[],
  deletedBaseId: string,
): CompanionBaseStampRecord | null {
  return (
    bases
      .filter((base) => base.kind === "home" && base.id !== deletedBaseId)
      .sort(compareCreatedAtDesc)[0] ?? null
  );
}

export function pickDecisionPlaces<T extends CompanionBaseSavedPlace>(
  places: T[],
  options: { activeBaseId: string | null },
): T[] {
  const conditionPlaces = sortPlacesByCreatedAtDesc(
    places.filter((place) => !place.isFestival),
  );

  if (!options.activeBaseId) return conditionPlaces;

  const scoped = conditionPlaces.filter((place) => place.baseId === options.activeBaseId);
  return scoped.length > 0 ? scoped : conditionPlaces;
}

export function buildBaseChipLabel(
  base: CompanionBaseStampRecord | null,
  options: ResolveBaseOptions = {},
): string {
  if (!base) return "Set your base";
  if (base.kind === "home") return `${base.name} · Home`;

  const now = options.now ?? new Date();
  const today = todayDateInTimeZone(base.timezone, now);

  if (base.startsOn && base.startsOn > today) {
    return `${base.name} · Trip from ${formatBaseDate(base.startsOn)}`;
  }
  if (base.endsOn) {
    return `${base.name} · Trip until ${formatBaseDate(base.endsOn)}`;
  }
  return `${base.name} · Trip`;
}

export function buildViewedBaseSummary(
  viewedBase: Pick<CompanionBaseRecord, "name">,
  activeBase: Pick<CompanionBaseRecord, "name"> | null,
): string {
  if (!activeBase) {
    return `Viewing ${viewedBase.name}. No active base right now.`;
  }
  return `Viewing ${viewedBase.name}. Your active base is ${activeBase.name}.`;
}

export function buildBaseGroups(options: GroupOptions): CompanionBaseGroup[] {
  const now = options.now ?? new Date();
  const groups: CompanionBaseGroup[] = [];
  const homeBases = options.bases
    .filter((base) => base.kind === "home")
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return compareCreatedAtDesc(a, b);
    });
  const tripBases = options.bases
    .filter((base) => base.kind === "trip")
    .sort(compareCreatedAtDesc);

  for (const base of [...homeBases, ...tripBases]) {
    const places = sortPlacesByCreatedAtDesc(
      options.places.filter((place) => place.baseId === base.id && !place.isFestival),
    );
    if (places.length === 0) continue;

    groups.push({
      key: `base:${base.id}`,
      heading: buildBaseGroupHeading(base, now),
      baseId: base.id,
      places,
    });
  }

  const elsewherePlaces = sortPlacesByCreatedAtDesc(
    options.places.filter((place) => !place.isFestival && !place.baseId),
  );
  if (elsewherePlaces.length > 0) {
    groups.push({
      key: "elsewhere",
      heading: "Elsewhere",
      baseId: null,
      places: elsewherePlaces,
    });
  }

  const eventPlaces = sortPlacesByCreatedAtDesc(
    options.places.filter((place) => place.isFestival),
  );
  if (eventPlaces.length > 0) {
    groups.push({
      key: "events",
      heading: "Events",
      baseId: null,
      places: eventPlaces,
    });
  }

  return groups;
}

export function baseLocalDateISO(
  timezone: string | null,
  now: Date,
): string {
  return todayDateInTimeZone(timezone, now);
}

export function findAssignablePlaceIds(
  places: Array<{
    id: string;
    baseId?: string | null;
    isFestival?: boolean;
    latitude?: number | null;
    longitude?: number | null;
  }>,
  base: { latitude: number | null; longitude: number | null } | null,
): string[] {
  if (!base || base.latitude === null || base.longitude === null) return [];
  const baseLat = base.latitude;
  const baseLng = base.longitude;

  return places
    .filter((place) => {
      if (place.isFestival) return false;
      if (place.baseId) return false;
      if (place.latitude == null || place.longitude == null) return false;
      return haversineKm(baseLat, baseLng, place.latitude, place.longitude) <= 90;
    })
    .map((place) => place.id);
}

function buildBaseGroupHeading(base: CompanionBaseRecord, now: Date): string {
  if (base.kind === "home") {
    return `Home · ${base.name}`;
  }

  const today = todayDateInTimeZone(base.timezone, now);
  if (base.startsOn && base.startsOn > today) {
    return `Trip · ${base.name} (from ${formatBaseDate(base.startsOn)})`;
  }
  if (base.endsOn) {
    return `Trip · ${base.name} (until ${formatBaseDate(base.endsOn)})`;
  }
  return `Trip · ${base.name}`;
}

function isTripActive(base: CompanionBaseStampRecord, now: Date): boolean {
  const today = todayDateInTimeZone(base.timezone, now);
  if (base.startsOn && base.startsOn > today) return false;
  if (base.endsOn && base.endsOn < today) return false;
  return true;
}

function todayDateInTimeZone(timezone: string | null, now: Date): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone ?? "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  }
}

function sortPlacesByCreatedAtDesc<T extends CompanionBaseSavedPlace>(
  places: T[],
): T[] {
  return [...places].sort((a, b) => {
    const diff = timestampMs(b.createdAt) - timestampMs(a.createdAt);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name, "en-GB");
  });
}

function compareCreatedAtDesc(
  a: Pick<CompanionBaseStampRecord, "createdAt">,
  b: Pick<CompanionBaseStampRecord, "createdAt">,
): number {
  return timestampMs(b.createdAt) - timestampMs(a.createdAt);
}

function timestampMs(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatBaseDate(value: string): string {
  const date = new Date(`${value}T00:00:00.000Z`);
  return BASE_DATE_FORMAT.format(date);
}
